import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useServices } from '@/shared/hooks/use-services';
import { useLocation } from 'wouter';
import { formatPrice } from '@/shared/utils/format-price';
import { cn } from '@/shared/lib/utils';
import {
  useCartQuantityPromotion,
  useStoreQuantityPromotion,
} from '@/modules/cart/hooks/use-cart-quantity-promotion';
import { useDuplicateCartLine } from '@/modules/cart/hooks/use-duplicate-cart-line';
import { QuantityPromotionBanner } from './quantity-promotion-banner';

interface CartSummaryProps {
  /** Logo da loja, ao lado do total. */
  storeLogo?: string | null;
}

/**
 * Rodapé da sacola na loja (PRAT-154): logo da loja, total e quantidade à
 * esquerda e um único botão cheio "Ver sacola" à direita, na cor da loja.
 * O total dá um pulinho quando entra item novo.
 */
export function CartSummary({ storeLogo }: CartSummaryProps) {
  const { listItems } = useServices();
  const [, navigate] = useLocation();

  const { data: cartItems } = useQuery({
    queryKey: ['cart-items'],
    queryFn: () => listItems.execute(),
    staleTime: 0, // Sempre fresh para carrinho
    gcTime: 5 * 60 * 1000, // 5 minutos
    refetchOnMount: true, // Força refetch ao montar para garantir dados atualizados
  });

  // Promoção por quantidade (PRAT-184): abate do total mostrado e avisa
  // quando falta pouco para ganhar o desconto.
  const {
    rule: quantityPromotionRule,
    categoryByItemId,
    categoryNames,
    wholesaleRule,
  } = useStoreQuantityPromotion();
  const quantityPromotion = useCartQuantityPromotion(
    cartItems,
    quantityPromotionRule,
    categoryByItemId,
    categoryNames,
    wholesaleRule
  );

  const duplicateLine = useDuplicateCartLine(categoryByItemId);
  const cheapestLine = quantityPromotion.cheapestLine;

  const total = Math.max(
    0,
    (cartItems?.reduce((acc, item) => acc + Number(item.total_price || 0), 0) || 0) -
      quantityPromotion.amount
  );
  const itemCount = cartItems?.reduce((acc, item) => acc + Number(item.quantity || 1), 0) || 0;

  const lastCount = useRef(itemCount);
  const [bump, setBump] = useState(false);
  useEffect(() => {
    if (itemCount > lastCount.current) {
      setBump(true);
      const t = setTimeout(() => setBump(false), 400);
      lastCount.current = itemCount;
      return () => clearTimeout(t);
    }
    lastCount.current = itemCount;
  }, [itemCount]);

  const goToCart = () => navigate('/cart/review-order');

  return (
    <div className="flex flex-col py-2">
      {quantityPromotion.amount > 0 && (
        <p data-testid="quantity-promotion-total" className="mb-1 text-xs font-bold text-green-600">
          {quantityPromotion.discountLabel} −{formatPrice(String(quantityPromotion.amount))}
        </p>
      )}
      {/* Chamada forte "Falta 1 item..." com botão que repete a linha mais barata */}
      {quantityPromotion.cta && (
        <QuantityPromotionBanner
          compact
          cta={quantityPromotion.cta}
          itemName={quantityPromotion.cheapestLine?.name}
          scope={quantityPromotion.scope}
          onAdd={cheapestLine ? () => duplicateLine.mutate(cheapestLine) : undefined}
          disabled={duplicateLine.isPending}
          className="mb-2"
        />
      )}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={goToCart}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          tabIndex={-1}
        >
          {storeLogo && (
            <img
              src={storeLogo}
              alt=""
              aria-hidden
              className="size-10 shrink-0 rounded-full border border-gray-100 bg-white object-contain"
            />
          )}
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="text-xs text-gray-500">Total sem entrega</span>
            <span
              className={cn(
                'origin-left truncate transition-transform duration-200',
                bump && 'scale-110'
              )}
            >
              <span className="text-base font-bold text-night">{formatPrice(String(total))}</span>
              <span className="text-xs text-gray-500">
                {` / ${itemCount} ${itemCount === 1 ? 'item' : 'itens'}`}
              </span>
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={goToCart}
          className="h-12 shrink-0 rounded-xl bg-primary px-6 text-[15px] font-bold text-primary-foreground transition-transform active:scale-[0.97] clickable-button"
        >
          Ver sacola
        </button>
      </div>
    </div>
  );
}
